<template>
    <file-pond
        ref="pond"
        class-name="file-pond-font-size-increase margin-above2x"
        label-file-processing-error="File error"
        :label-idle="labelIdle"
        :disabled="disabled"
        :accepted-file-types="acceptedFileTypes"
        :file-validate-type-label-expected-types-map="fileValidateTypeLabelExpectedTypesMap"
        @processfileprogress="(file, progress) => changeProgress(progress)"
        @removefile="setInitialState"
        :server="{
            process: (fieldName, file, metadata, load, error) => postFile(file, load, error, true),
            revert: null,
            fetch: null,
        }"
    />
</template>

<script>
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.min.css'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import apiRequest from 'src/mixins/api-request.js'
import Constants from "@/fileuploader/constants.js"

const FilePond = vueFilePond(FilePondPluginFileValidateType)

export default {
    name: "AristotleFilePond",
    components: {
        FilePond,
    },
    mixins: [apiRequest],
    props: {
        url: {
            type: String,
            required: true,
        },
        statusUrl: {
            type: String,
            required: true,
        },
        labelIdle: {
            type: String,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        currentTags: {
            type: Array,
            default: () => [],
        },
        selectedWorkGroup: {
            type: Number,
            default: null,
        },
        contentType: {
            type: String,
            default: '',
        },
        itemId: {
            type: String,
            default: '',
        },
    },
    data: () => ({
        acceptedFileTypes: Constants.acceptedFileTypes,
        fileValidateTypeLabelExpectedTypesMap: Constants.fileValidateTypeLabelExpectedTypesMap,
        file: null,
        filePondError: null,
        interval: null,
        pending: false,
        started: null,
        pollTime: 5000,  // period to call checkStatus in ms
        timeout: 30000,  // period after which failure assumed if still pending
    }),
    methods: {
        submitFile: function () {
            this.postFile(this.file, null, this.filePondError, false)
        },
        postFile: function (file, load, error, dry_run) {
            this.$emit('changeErrors', {})
            this.file = file
            this.filePondError = error
            this.$emit('changeListOfMetadataRepresentations', [])
            this.$emit('changeExcelToCsvMessage', '')
            if (dry_run) {
                this.$emit('changeLoadingMessage', 'Reading file...')
            } else {
                this.$emit('changeLoadingMessage', 'Creating objects...')
                this.$emit('changeFileIsLoaded', false)
            }
            this.$emit('changeIsLoading', true)
            let selectedWorkGroupFormData
            if (this.selectedWorkGroup === null || this.selectedWorkGroup === undefined) {
                selectedWorkGroupFormData = ''
            } else{
                selectedWorkGroupFormData = this.selectedWorkGroup
            }
            const formData = new FormData()
            formData.append('file', file)
            formData.append('dry_run', dry_run)
            formData.append('tags', JSON.stringify(this.currentTags))
            formData.append('workgroup_id', selectedWorkGroupFormData)
            formData.append('content_type', this.contentType)
            if (this.itemId) {
                formData.append('item_id', this.itemId)
            }

            this.post(
                this.url, formData
            ).then((response) => {
                if (response.data.unauthenticated) {
                    this.$emit('changeNumberOfSteps', 1)
                    if (response.data.errors.length) {
                        this.$emit('changeErrors', response.data.errors)
                        this.filePondError()
                    } else {
                        this.$emit('changeListOfMetadataRepresentations', response.data.data)
                        this.$emit('changeShowLoginMessage', true)
                    }
                    this.$emit('changeIsLoading', false)
                } else {
                    this.interval = setInterval(() => this.checkTaskStatus(response.data.data), this.pollTime)
                    this.pending = true
                    this.started = Date.now()
                }
                if (load) {
                    // passing the file id to Filepond
                    load(response.data.data)
                    this.$emit('changeExcelToCsvMessage', response.data.message)
                }
            }).catch((e) => {
                if (e.response && [400, 415].includes(e.response.status)) {
                    this.$emit('changeErrors', e.response.data)
                } else if (e.response) {
                    // Other error responses are handled by the api-request.js mixin.
                    this.setRequestError('Request could not be completed.')
                }
                this.filePondError()
                this.$emit('changeIsLoading', false)
            })
        },
        checkTaskStatus: function (taskId) {
            this.get(this.getTaskStatusUrl(taskId)).then((response) => {
                if (response.data.state !== 'PENDING') {
                    this.pending = false
                }
                if (response.data.is_ready) {
                    if (response.data.result.dry_run) {  // If was run in dry_run mode.
                        if (response.data.result.errors.length > 0) {
                            this.$emit('changeErrors', response.data.result.errors)
                            this.filePondError()
                        } else {
                            this.$emit('changeFileIsLoaded', true)
                            this.$emit('changeListOfMetadataRepresentations', response.data.result.data)
                        }
                    } else if (response.data.result.dry_run === false) {
                        if (response.data.result.errors.length > 0) {
                            this.$emit('changeErrors', response.data.result.errors)
                            this.filePondError()
                        } else {  // Metadata was successfully created.
                            this.$emit('changeProgress', 2)
                            this.$emit('changeListOfMetadataRepresentations', response.data.result.data)
                        }
                        this.$emit('changeFileIsLoaded', false)
                    } else if (response.data.state === 'FAILURE') {
                        this.$emit('changeErrors', response.data.result)
                        this.filePondError()
                        this.$emit('changeProgress', 0)
                    }
                    clearInterval(this.interval)
                    this.$emit('changeIsLoading', false)
                }
            }).catch((e) => {
                if (e.response && e.response.status === 500) {
                    // Other error responses are handled by the api-request.js mixin.
                    this.setRequestError('Request could not be completed.')
                } else {
                    this.$emit('changeErrors', e)
                }
                clearInterval(this.interval)
                this.filePondError()
                this.$emit('changeIsLoading', false)
            })
            // Check for timeout if we are still pending
            if (this.pending) {
                let time = Date.now()
                if ((time - this.started) > this.timeout) {
                    this.ready = true
                    clearInterval(this.interval)
                }
            }
        },
        getCustomMIMEType: (source, type) => new Promise((resolve) => {
            // If the user uses window as the platform and the mime type is empty and the ext is csv, set it to csv
            const is_valid_ext = ['csv'].includes(source.name.split('.').pop())
            const is_window_os = window.navigator.platform.indexOf('Win') > -1
            if (is_window_os && !type && is_valid_ext ) type = 'text/csv'
            resolve(type);
        }),
        getTaskStatusUrl: function (taskId) {
            return this.statusUrl.replace('{taskid}', taskId)
        },
        changeProgress: function (progress) {
            this.$emit('changeProgress', progress)
        },
        setInitialState: function () {
            this.$emit('changeListOfMetadataRepresentations', [])
            this.$emit('changeFileIsLoaded', false)
            this.$emit('changeIsLoading', false)
            this.$emit('changeErrors', {})
            this.$emit('changeProgress', 0)
            this.$emit('changeTags', [])
            this.$emit('changeSelectedWorkgroup', null)
            this.$emit('changeExcelToCsvMessage', '')
            this.$emit('changeShowLoginMessage', false)
            this.file = null
        },
        removeFile: function () {
            this.$refs.pond.removeFile()
        },
    },
}
</script>

<style scoped>

</style>
